import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, FolderTree, AlertCircle, Check, Wand2, Plus, ArrowRight, Send, User, Bot, MessageSquare } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

interface SuggestedBucket {
  name: string;
  description?: string;
  skills: string[];
  isNew?: boolean;
}

interface SkillAssignment {
  skillName: string;
  currentBucket: string | null;
  suggestedBucket: string;
  reason: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AITaxonomyOrganizerProps {
  existingSkills: { id: string; name: string; parent_id: string | null; type: string | null }[];
  existingBuckets: { id: string; name: string }[];
  onApplyTaxonomy: (buckets: { name: string; skills: string[]; isNew?: boolean }[]) => void;
}

const AITaxonomyOrganizer = ({ existingSkills, existingBuckets, onApplyTaxonomy }: AITaxonomyOrganizerProps) => {
  const [loading, setLoading] = useState(false);
  const [suggestedBuckets, setSuggestedBuckets] = useState<SuggestedBucket[]>([]);
  const [skillAssignments, setSkillAssignments] = useState<SkillAssignment[]>([]);
  const [selectedBuckets, setSelectedBuckets] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: 'Hi! I can help organize your skills into meaningful categories. Click "Organize Skills" to start, or tell me what you\'d like first (e.g., "Focus on backend technologies" or "Create broad categories").',
      timestamp: new Date()
    }
  ]);
  const [userInput, setUserInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const skillNames = existingSkills.map(s => s.name);
  const bucketNames = existingBuckets.map(b => b.name);
  const unorganizedSkills = existingSkills.filter(s => !s.parent_id);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchTaxonomySuggestions = async (userInstruction?: string) => {
    if (skillNames.length === 0) {
      toast({ title: 'No skills to organize', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setError(null);
    
    // Only reset suggestions if this is a fresh start (no user instruction)
    if (!userInstruction) {
      setSuggestedBuckets([]);
      setSkillAssignments([]);
      setSelectedBuckets(new Set());
    }

    try {
      const unorganizedNames = unorganizedSkills.map(s => s.name);
      const limitedUnorganized = unorganizedNames.slice(0, 200);
      const limitedSkillNames = skillNames.slice(0, 300);

      // Build conversation history for context
      const conversationHistory = chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const { data, error: fnError } = await supabase.functions.invoke('suggest-skills', {
        body: { 
          existingSkills: limitedSkillNames, 
          existingBuckets: bucketNames,
          unorganizedSkills: limitedUnorganized,
          action: 'organize_taxonomy',
          userInstruction,
          conversationHistory,
          currentSuggestions: suggestedBuckets.length > 0 ? suggestedBuckets : undefined
        }
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const buckets = data?.suggestedBuckets || [];
      const assignments = data?.skillAssignments || [];
      const aiResponse = data?.assistantMessage || 'Here are my suggestions based on your skills.';
      
      setSuggestedBuckets(buckets);
      setSkillAssignments(assignments);
      setSelectedBuckets(new Set(buckets.map((b: SuggestedBucket) => b.name)));

      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      }]);
    } catch (err: any) {
      console.error('Error fetching taxonomy suggestions:', err);
      setError(err.message || 'Failed to get taxonomy suggestions');
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.message || 'Unknown error'}`,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || loading) return;

    const message = userInput.trim();
    setUserInput('');

    // Add user message to chat
    setChatMessages(prev => [...prev, {
      role: 'user',
      content: message,
      timestamp: new Date()
    }]);

    // Fetch new suggestions with the instruction
    await fetchTaxonomySuggestions(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleBucket = (bucketName: string) => {
    const newSelected = new Set(selectedBuckets);
    if (newSelected.has(bucketName)) {
      newSelected.delete(bucketName);
    } else {
      newSelected.add(bucketName);
    }
    setSelectedBuckets(newSelected);
  };

  const handleApply = () => {
    const bucketsToApply = suggestedBuckets.filter(b => selectedBuckets.has(b.name));
    onApplyTaxonomy(bucketsToApply);
    toast({ title: `Applied ${bucketsToApply.length} category buckets` });
  };

  const newBuckets = suggestedBuckets.filter(b => b.isNew);
  const existingBucketSuggestions = suggestedBuckets.filter(b => !b.isNew);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FolderTree className="h-5 w-5 text-emerald-500" />
          <h3 className="font-semibold">AI Taxonomy Organizer</h3>
        </div>
        <Button onClick={() => fetchTaxonomySuggestions()} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Wand2 className="h-4 w-4 mr-2" />
              Organize Skills
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4 text-center">
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-2xl font-bold">{skillNames.length}</p>
          <p className="text-xs text-muted-foreground">Total Skills</p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-2xl font-bold">{unorganizedSkills.length}</p>
          <p className="text-xs text-muted-foreground">Unorganized</p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-2xl font-bold">{bucketNames.length}</p>
          <p className="text-xs text-muted-foreground">Existing Buckets</p>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="mb-4 border rounded-lg bg-muted/20">
          <div className="flex items-center gap-2 p-3 border-b bg-muted/30">
            <MessageSquare className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Chat with AI Organizer</span>
          </div>
          
          <ScrollArea className="h-48 p-3">
            <div className="space-y-3">
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-3.5 w-3.5 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    {msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <User className="h-3.5 w-3.5" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-2 items-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          <div className="p-3 border-t">
            <div className="flex gap-2">
              <Input
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Give instructions (e.g., 'Focus on software development' or 'Create fewer, broader categories')"
                disabled={loading}
                className="flex-1"
              />
              <Button onClick={handleSendMessage} disabled={loading || !userInput.trim()} size="icon">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Refine the organization by giving specific instructions
            </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm mb-4">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {suggestedBuckets.length > 0 && (
        <>
          {newBuckets.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="h-4 w-4 text-green-500" />
                <h4 className="font-medium text-sm">New Buckets to Create</h4>
                <Badge variant="secondary" className="text-xs">{newBuckets.length} new</Badge>
              </div>
              <Accordion type="multiple" defaultValue={newBuckets.map(b => b.name)}>
                {newBuckets.map((bucket) => (
                  <AccordionItem key={bucket.name} value={bucket.name} className="border-green-500/30">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedBuckets.has(bucket.name)}
                        onCheckedChange={() => toggleBucket(bucket.name)}
                      />
                      <AccordionTrigger className="flex-1 hover:no-underline">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-green-500 text-green-600 text-xs">NEW</Badge>
                          <span className="font-medium">{bucket.name}</span>
                          <Badge variant="secondary" className="text-xs">{bucket.skills.length} skills</Badge>
                        </div>
                      </AccordionTrigger>
                    </div>
                    <AccordionContent>
                      {bucket.description && (
                        <p className="text-sm text-muted-foreground mb-2">{bucket.description}</p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {bucket.skills.map((skill, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {existingBucketSuggestions.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowRight className="h-4 w-4 text-blue-500" />
                <h4 className="font-medium text-sm">Organize into Existing Buckets</h4>
              </div>
              <Accordion type="multiple" defaultValue={existingBucketSuggestions.map(b => b.name)}>
                {existingBucketSuggestions.map((bucket) => (
                  <AccordionItem key={bucket.name} value={bucket.name}>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedBuckets.has(bucket.name)}
                        onCheckedChange={() => toggleBucket(bucket.name)}
                      />
                      <AccordionTrigger className="flex-1 hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{bucket.name}</span>
                          <Badge variant="secondary" className="text-xs">{bucket.skills.length} skills to add</Badge>
                        </div>
                      </AccordionTrigger>
                    </div>
                    <AccordionContent>
                      {bucket.description && (
                        <p className="text-sm text-muted-foreground mb-2">{bucket.description}</p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {bucket.skills.map((skill, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-sm text-muted-foreground">
              {selectedBuckets.size} categories selected ({newBuckets.filter(b => selectedBuckets.has(b.name)).length} new)
            </span>
            <Button onClick={handleApply} disabled={selectedBuckets.size === 0}>
              <Check className="h-4 w-4 mr-2" />
              Apply Taxonomy
            </Button>
          </div>
        </>
      )}

      {!loading && suggestedBuckets.length === 0 && !error && (
        <div className="text-center py-6 border rounded-md bg-muted/30">
          <FolderTree className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Click "Organize Skills" to start. You can then chat with the AI to refine the suggestions.
          </p>
        </div>
      )}
    </Card>
  );
};

export default AITaxonomyOrganizer;
